/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   devRole.ts
 *     Description     :   Local-only role override for testing role-gated UI under DEV_AUTH_BYPASS.
 *                         Persisted to localStorage; ignored entirely outside dev bypass (the real
 *                         DB role is authoritative). Lets the operator preview Administrator /
 *                         Operator / Viewer without real Entra assignments.
 *     Module          :   user-management (frontend/auth)
 *     Component Type  :   Store
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-06-25      Peter Dobler        Initial creation (dev role selector)
 * -----------------------------------------------------------------------------------------
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     useDevRole
 *
 * =========================================================================================
 */

import { create } from "zustand";
import type { AppRole } from "@nextgenmigration/shared/user";

const STORAGE_KEY = "ngm.devRole";

/** null = use the real DB role (default). Only honored under DEV_AUTH_BYPASS. */
export type DevRole = AppRole | null;

function read(): DevRole {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "Administrator" || v === "Operator" || v === "Viewer" ? v : null;
}

interface DevRoleState {
  role: DevRole;
  setRole: (role: DevRole) => void;
}

export const useDevRole = create<DevRoleState>()((set) => ({
  role: read(),
  setRole: (role) => {
    if (typeof window !== "undefined") {
      if (role) window.localStorage.setItem(STORAGE_KEY, role);
      else window.localStorage.removeItem(STORAGE_KEY);
    }
    set({ role });
  },
}));
